import type {
  Class,
  DataRecord,
  Datum,
  FlatDataArray,
  FlatDataRecord,
} from '@suntex.core/types';

export type ContextFrame = string | ClassContextFrame;

export type ClassContextFrame = Class<object> | (() => Class<object>);

export enum LogLevel {
  Fatal = 1,
  Error = 2,
  Warn = 3,
  Info = 4,
  Debug = 5,
  Trace = 6,
}

export type LogMethod = (e: LogEntry) => void;

export interface LogEntry {
  level: LogLevel;
  message: string;
  context?: string;
  error?: unknown;
  data?: UnknownLogDataProvider;
}

export type StaticLogData = DataRecord;

export type StrictStaticLogData = Record<
  string,
  Datum | FlatDataArray | FlatDataRecord | undefined
>;

export type UnknownLogDataProvider =
  | StaticLogData
  | (() => Record<string, unknown> | undefined);

export interface BasicLogger {
  log: LogMethod;
}

export interface StandardLogger {
  fatal(message: string, data: UnknownLogDataProvider, error?: unknown): void;

  error(message: string, data: UnknownLogDataProvider, error?: unknown): void;

  warn(message: string, data?: StaticLogData, error?: unknown): void;

  info(message: string, data?: StrictStaticLogData): void;

  debug(message: string, data?: StaticLogData): void;

  trace(data: UnknownLogDataProvider): void;
}

export type LoggerOpts = Partial<{ level: LogLevel }>;

export interface ContextualLogger extends StandardLogger {
  for(context: string[], opts?: LoggerOpts): ContextualLogger;
  for<T extends object>(
    context: () => Class<T>,
    opts?: LoggerOpts,
  ): ClassLogger<T>;
}

export interface ClassLogger<T extends object> extends ContextualLogger {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forMethod(method: (this: T, ...args: any[]) => any): StandardLogger;
}
